import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { DEFAULTS } from '@/lib/defaults'
import { fetchAllSiteContent } from '@/lib/queries/admin'

const ContentContext = createContext(null)

export function ContentProvider({ children }) {
  const [contentMap, setContentMap] = useState(new Map())
  const [loading, setLoading] = useState(true)

  const fetchContent = useCallback(async () => {
    try {
      const data = await fetchAllSiteContent()
      const map = new Map()
      for (const row of data) {
        map.set(row.section_key, row)
      }
      setContentMap(map)
    } catch {
      // On error, contentMap stays empty — components will use defaults
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchContent()
  }, [fetchContent])

  return (
    <ContentContext.Provider value={{ contentMap, loading, refetch: fetchContent }}>
      {children}
    </ContentContext.Provider>
  )
}

export function useContent(sectionKey, { draft = false } = {}) {
  const ctx = useContext(ContentContext)
  const defaults = DEFAULTS[sectionKey] || {}

  // Gracefully return defaults if rendered outside ContentProvider
  if (!ctx) {
    return { content: defaults, loading: false, isVisible: true, isPublished: false }
  }

  const { contentMap, loading } = ctx
  const row = contentMap.get(sectionKey)

  if (!row) {
    return { content: defaults, loading, isVisible: true, isPublished: false }
  }

  if (!row.is_visible && !draft) {
    return { content: null, loading, isVisible: false, isPublished: row.is_published }
  }

  const rawContent = draft ? row.content_draft : row.content_published
  const content = { ...defaults, ...rawContent }

  return {
    content,
    loading,
    isVisible: row.is_visible,
    isPublished: row.is_published,
  }
}

export function useContentContext() {
  const ctx = useContext(ContentContext)
  if (!ctx) throw new Error('useContentContext must be used within ContentProvider')
  return ctx
}
